import { useState, useEffect, useRef } from 'react';
import { ToastContainer } from 'react-toastify';

import { TEST_CHAT_SUBTASK, TEST_CHAT_PROJECT, BOT_ID } from '@scale/shared/defaults';

import { AttemptBehaviorLogContextProvider } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/AttemptBehaviorLogContext';

import { IChatTaskFeatureConfig } from '@scale/shared/types/config';
import { useChatLayoutStore, ChatLayout } from '../hooks/useChatLayoutStore';
import { InteractionDemoInterface } from './InteractionDemoInterface';
import { ConfigPage } from './pages/ConfigPage';
import { DemoProviders } from './DemoProviders';
import TaskPreview from './pages/TaskPreview';
import { TaskAudit } from './pages/TaskAudit';
import { ResponseTemplating } from './pages/ResponseTemplating';
import { ChatInteractionHandle } from '../ChatInteraction';
import { QueryClient, QueryClientProvider } from 'react-query';

export const ThreadedInteractionDemo: React.FC = () => {
  return (
    <AttemptBehaviorLogContextProvider
      workerId={BOT_ID}
      assignmentId={TEST_CHAT_SUBTASK}
      projectId={TEST_CHAT_PROJECT}
    >
      <ThreadedInteractionDemoImpl />
    </AttemptBehaviorLogContextProvider>
  );
};

export const StandaloneThreadedInteractionDemo = () => {
  const [queryClient] = useState(new QueryClient());
  return (
    <QueryClientProvider client={queryClient}>
      <ThreadedInteractionDemo />
    </QueryClientProvider>
  );
};

const ThreadedInteractionDemoImpl: React.FC = () => {
  const [chatTaskFeatureConfig, setChatTaskFeatureConfig] = useState<IChatTaskFeatureConfig>({});
  const [restrictRollbacks, setRestrictRollbacks] = useState<boolean>(false);
  const chatEntrypointRef = useRef<ChatInteractionHandle | null>(null);

  function getEventBus() {
    return chatEntrypointRef.current?.eventBus;
  }

  const setLayout = useChatLayoutStore(state => state.setLayout);
  useEffect(() => {
    setLayout(ChatLayout.Inline);
  }, [setLayout]);

  return (
    <div className="scaleui scale-tailwind scale-theme h-full">
      <DemoProviders
        restrictRollbacks={restrictRollbacks}
        chatTaskFeatureConfig={chatTaskFeatureConfig}
      >
        <InteractionDemoInterface getEventBus={getEventBus}>
          <ConfigPage
            restrictRollbacks={restrictRollbacks}
            chatTaskFeatureConfig={chatTaskFeatureConfig}
            setRestrictRollbacks={setRestrictRollbacks}
            setChatTaskFeatureConfig={setChatTaskFeatureConfig}
          />
          <TaskPreview ref={chatEntrypointRef} chatTaskFeatureConfig={chatTaskFeatureConfig} />
          <TaskAudit />
          <ResponseTemplating />
          <ToastContainer />
        </InteractionDemoInterface>
      </DemoProviders>
    </div>
  );
};
